/**
 * EventTransportSection
 *
 * Transport/car assignment section for events.
 * Allows adding and managing transport arrangements.
 */

import { useState, useCallback } from 'react';
import { StyleSheet, Text, TextInput, TouchableOpacity, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Colors, ModernPalette } from '@/constants/theme';
import { Spacing, BorderRadius } from '@/constants/spacing';
import { getBodyFont } from '@/constants/typography';
import type { TransportAssignment, TransportMode } from '@/types/transport';
import { TRANSPORT_MODES, getTransportModeConfig } from '@/types/transport';
import { EventTransportManager } from '@/lib/managers/EventTransportManager';
import type { FamilyMember } from '@/types/family-member';

type EventTransportSectionProps = {
  transport: TransportAssignment[];
  onChange: (transport: TransportAssignment[]) => void;
  familyMembers: FamilyMember[];
  palette: typeof Colors.light;
};

export function EventTransportSection({
  transport,
  onChange,
  familyMembers,
  palette,
}: EventTransportSectionProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [selectedMode, setSelectedMode] = useState<TransportMode>('car');
  const [vehicleName, setVehicleName] = useState('');
  const [selectedDriverId, setSelectedDriverId] = useState<string | undefined>();
  const [selectedPassengerIds, setSelectedPassengerIds] = useState<string[]>([]);

  const handleAddTransport = useCallback(() => {
    const newAssignment = EventTransportManager.createAssignment(selectedMode, {
      vehicleName: vehicleName || undefined,
      driverFamilyMemberId: selectedDriverId,
      passengerFamilyMemberIds: selectedPassengerIds,
    });
    onChange([...transport, newAssignment]);

    // Reset form
    setVehicleName('');
    setSelectedDriverId(undefined);
    setSelectedPassengerIds([]);
    setIsExpanded(false);
  }, [selectedMode, vehicleName, selectedDriverId, selectedPassengerIds, transport, onChange]);

  const handleRemoveTransport = useCallback((assignmentId: string) => {
    onChange(EventTransportManager.removeAssignment(transport, assignmentId));
  }, [transport, onChange]);

  const togglePassenger = useCallback((memberId: string) => {
    setSelectedPassengerIds(prev =>
      prev.includes(memberId)
        ? prev.filter(id => id !== memberId)
        : [...prev, memberId]
    );
  }, []);

  return (
    <View style={styles.container}>
      {/* Header */}
      <TouchableOpacity
        style={styles.header}
        onPress={() => setIsExpanded(!isExpanded)}
        activeOpacity={0.7}
      >
        <View style={styles.iconContainer}>
          <Ionicons name="car-outline" size={18} color={palette.textMuted} />
        </View>
        <Text style={[styles.headerText, { color: palette.text, fontFamily: getBodyFont() }]}>
          Transport
        </Text>
        {transport.length > 0 && (
          <View style={[styles.badge, { backgroundColor: palette.primary }]}>
            <Text style={styles.badgeText}>{transport.length}</Text>
          </View>
        )}
        <Ionicons
          name={isExpanded ? 'chevron-up' : 'chevron-down'}
          size={18}
          color={palette.textMuted}
        />
      </TouchableOpacity>

      {/* Existing transport assignments */}
      {transport.length > 0 && (
        <View style={styles.assignmentsList}>
          {transport.map(assignment => {
            const config = getTransportModeConfig(assignment.mode);
            return (
              <View key={assignment.id} style={[styles.assignmentCard, { borderColor: palette.border }]}>
                <Ionicons name={config.icon as any} size={16} color={palette.primary} />
                <View style={styles.assignmentInfo}>
                  <Text style={[styles.assignmentTitle, { color: palette.text }]}>
                    {assignment.vehicleName || config.label}
                  </Text>
                  {assignment.passengerFamilyMemberIds.length > 0 && (
                    <Text style={[styles.assignmentSubtitle, { color: palette.textMuted }]}>
                      {assignment.passengerFamilyMemberIds.length} passenger(s)
                    </Text>
                  )}
                </View>
                <TouchableOpacity
                  onPress={() => handleRemoveTransport(assignment.id)}
                  hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
                >
                  <Ionicons name="close-circle" size={20} color={palette.textMuted} />
                </TouchableOpacity>
              </View>
            );
          })}
        </View>
      )}

      {/* Expanded form */}
      {isExpanded && (
        <View style={[styles.form, { borderColor: palette.border }]}>
          {/* Transport mode selector */}
          <Text style={[styles.formLabel, { color: palette.text }]}>Mode</Text>
          <View style={styles.modeSelector}>
            {TRANSPORT_MODES.map(mode => (
              <TouchableOpacity
                key={mode.mode}
                style={[
                  styles.modeButton,
                  selectedMode === mode.mode && { backgroundColor: palette.primary },
                  { borderColor: palette.border },
                ]}
                onPress={() => setSelectedMode(mode.mode)}
              >
                <Ionicons
                  name={mode.icon as any}
                  size={18}
                  color={selectedMode === mode.mode ? '#FFFFFF' : palette.textMuted}
                />
              </TouchableOpacity>
            ))}
          </View>

          {/* Vehicle name input */}
          <Text style={[styles.formLabel, { color: palette.text }]}>Vehicle name (optional)</Text>
          <TextInput
            style={[styles.input, { color: palette.text, borderColor: palette.border }]}
            value={vehicleName}
            onChangeText={setVehicleName}
            placeholder="e.g., Mom's Car"
            placeholderTextColor={palette.textMuted}
          />

          {/* Driver picker */}
          <Text style={[styles.formLabel, { color: palette.text }]}>Driver</Text>
          <View style={styles.memberPicker}>
            <TouchableOpacity
              style={[
                styles.memberChip,
                !selectedDriverId && { backgroundColor: palette.border },
              ]}
              onPress={() => setSelectedDriverId(undefined)}
            >
              <Text style={{ color: !selectedDriverId ? palette.text : palette.textMuted, fontSize: 13 }}>
                None
              </Text>
            </TouchableOpacity>
            {familyMembers.map(member => (
              <TouchableOpacity
                key={member.id}
                style={[
                  styles.memberChip,
                  selectedDriverId === member.id && { backgroundColor: member.color || palette.primary },
                ]}
                onPress={() => setSelectedDriverId(member.id)}
              >
                <Text
                  style={{
                    color: selectedDriverId === member.id ? '#FFFFFF' : palette.text,
                    fontSize: 13,
                  }}
                >
                  {member.name}
                </Text>
              </TouchableOpacity>
            ))}
          </View>

          {/* Passengers picker */}
          <Text style={[styles.formLabel, { color: palette.text }]}>Passengers</Text>
          <View style={styles.memberPicker}>
            {familyMembers.map(member => {
              const isSelected = selectedPassengerIds.includes(member.id);
              return (
                <TouchableOpacity
                  key={member.id}
                  style={[
                    styles.memberChip,
                    isSelected && { backgroundColor: member.color || palette.primary },
                  ]}
                  onPress={() => togglePassenger(member.id)}
                >
                  <Text
                    style={{
                      color: isSelected ? '#FFFFFF' : palette.text,
                      fontSize: 13,
                    }}
                  >
                    {member.name}
                  </Text>
                </TouchableOpacity>
              );
            })}
          </View>

          {/* Add button */}
          <TouchableOpacity
            style={[styles.addButton, { backgroundColor: palette.primary }]}
            onPress={handleAddTransport}
          >
            <Text style={styles.addButtonText}>Add Transport</Text>
          </TouchableOpacity>
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    paddingHorizontal: Spacing.lg,
    paddingVertical: Spacing.sm,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  iconContainer: {
    width: 40,
    alignItems: 'center',
  },
  headerText: {
    flex: 1,
    fontSize: 14,
  },
  badge: {
    minWidth: 20,
    height: 20,
    borderRadius: 10,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: Spacing.sm,
    paddingHorizontal: 6,
  },
  badgeText: {
    color: '#FFFFFF',
    fontSize: 13,
    fontWeight: '600',
  },
  assignmentsList: {
    marginTop: Spacing.sm,
    marginLeft: 40,
    gap: Spacing.xs,
  },
  assignmentCard: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: Spacing.sm,
    borderRadius: BorderRadius.sm,
    borderWidth: 1,
    gap: Spacing.sm,
  },
  assignmentInfo: {
    flex: 1,
  },
  assignmentTitle: {
    fontSize: 14,
    fontWeight: '500',
  },
  assignmentSubtitle: {
    fontSize: 12,
  },
  form: {
    marginTop: Spacing.md,
    marginLeft: 40,
    padding: Spacing.md,
    borderRadius: BorderRadius.md,
    borderWidth: 1,
    gap: Spacing.sm,
  },
  formLabel: {
    fontSize: 13,
    fontWeight: '600',
    marginBottom: 4,
  },
  modeSelector: {
    flexDirection: 'row',
    gap: Spacing.xs,
    marginBottom: Spacing.sm,
  },
  modeButton: {
    width: 40,
    height: 40,
    borderRadius: BorderRadius.sm,
    borderWidth: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  input: {
    borderWidth: 1,
    borderRadius: BorderRadius.sm,
    padding: Spacing.sm,
    fontSize: 14,
    marginBottom: Spacing.sm,
  },
  memberPicker: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: Spacing.xs,
    marginBottom: Spacing.sm,
  },
  memberChip: {
    paddingHorizontal: Spacing.sm,
    paddingVertical: Spacing.xs,
    borderRadius: BorderRadius.full,
    backgroundColor: ModernPalette.lightGray,
  },
  addButton: {
    paddingVertical: Spacing.sm,
    borderRadius: BorderRadius.sm,
    alignItems: 'center',
    marginTop: Spacing.sm,
  },
  addButtonText: {
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: '600',
  },
});
